import { jalonsPaiement, type VitrineCatalogueKind, type VitrineLanguage } from "@/lib/vitrine/catalogue";
import type { VitrinePricing, VitrinePricingLine } from "@/lib/vitrine/types";

// Les trois grands jalons d'un site vitrine, qui structurent À LA FOIS le
// déroulé (étape par étape, détaillé) et le devis (postes regroupés par jalon,
// sous-total par jalon) : décision Thibault 2026-09-27. Durée fixe de deux
// semaines (10 jours ouvrés).
//
// Le détail n'est pas généré : il est écrit ici, et chaque action n'apparaît
// que si le poste correspondant figure dans le devis de CE prospect (pas de
// redirections sans refonte, pas de formulaire s'il n'y en a pas). Les encadrés
// "Ce qui change avec Léman IT" ne reprennent que des engagements réels (voir
// faits.ts), à relire par Thibault.

export const DUREE_SEMAINES = 2;

export type JalonId = "cadrage" | "construction" | "lancement";

export interface Jalon {
  id: JalonId;
  numero: number;
  jours: string;
  titre: string;
  intro: string;
  actions: string[];
  vousValidez: string | null;
  paiements: string[]; // "30 % à la commande", ...
  process: { titre: string; texte: string }[]; // ce qui distingue Léman IT
}

// Rattachement de chaque poste du catalogue à un jalon.
const JALON_PAR_LOT: Record<string, JalonId> = {
  // standard
  cadrage: "cadrage",
  "maquette-accueil": "cadrage",
  "integration-accueil": "construction",
  "page-contenu": "construction",
  "back-office": "construction",
  "formulaire-contact": "construction",
  "formulaire-metier": "construction",
  estimateur: "construction",
  galerie: "construction",
  carte: "construction",
  blog: "construction",
  "reprise-contenu": "construction",
  legal: "construction",
  "seo-base": "lancement",
  "seo-local": "lancement",
  "visibilite-ia": "lancement",
  redirections: "lancement",
  "mise-en-ligne": "lancement",
  "prise-en-main": "lancement",
  "prospects-b2b": "lancement",
  // wordpress
  "wp-audit": "cadrage",
  "wp-sauvegarde-preprod": "cadrage",
  "wp-mises-a-jour": "construction",
  "wp-nettoyage": "construction",
  "wp-arborescence": "construction",
  "wp-accueil": "construction",
  "wp-page": "construction",
  "wp-section": "construction",
  "wp-correction": "construction",
  "wp-formulaire": "construction",
  "wp-performance": "construction",
  "wp-blog": "construction",
  "wp-seo-base": "lancement",
  "wp-seo-local": "lancement",
  "wp-mise-en-production": "lancement",
  "wp-prise-en-main": "lancement",
};

export function jalonDuLot(lotId: string): JalonId {
  return JALON_PAR_LOT[lotId.split(":")[0]] ?? "construction";
}

function paiements(kind: VitrineCatalogueKind, lang: VitrineLanguage = "fr") {
  const [commande, milieu, fin] = jalonsPaiement(kind, lang);
  const txt = (p: { part: number; label: string }) =>
    `${p.part}${lang === "en" ? "%" : " %"} ${p.label.charAt(0).toLowerCase()}${p.label.slice(1)}`;
  return { commande: txt(commande), milieu: txt(milieu), fin: txt(fin) };
}

export function buildJalons(
  kind: VitrineCatalogueKind,
  pricing: VitrinePricing | null,
  lang: VitrineLanguage = "fr"
): Jalon[] {
  const base = new Set(
    (pricing?.lines ?? []).filter((l) => l.kind === "base").map((l) => l.lotId.split(":")[0])
  );
  const has = (id: string) => base.has(id);
  const p = paiements(kind, lang);
  const when = (cond: boolean, text: string) => (cond ? [text] : []);
  if (lang === "en") return jalonsEn(kind, has, p, when);

  if (kind === "wordpress") {
    return [
      {
        id: "cadrage",
        numero: 1,
        jours: "Jours 1 et 2",
        titre: "Audit et site de test",
        intro: "Avant de toucher à quoi que ce soit, je sécurise votre site et je mesure où il en est.",
        actions: [
          "Sauvegarde complète de votre site : fichiers, base de données, réglages.",
          "Copie du site sur un sous-domaine protégé et non indexé : c'est là que je travaille, votre site en ligne n'est jamais perturbé.",
          "Audit du thème, des extensions et de la vitesse de chargement, avec les mesures de Google.",
          "Plan d'intervention écrit, poste par poste, à partir de votre liste de demandes.",
        ],
        vousValidez: "Le plan d'intervention.",
        paiements: [p.commande, p.milieu],
        process: [
          {
            titre: "Une sauvegarde avant tout",
            texte: "Rien n'est modifié sur votre site tant qu'une copie complète n'existe pas. En cas de problème, on revient en arrière.",
          },
        ],
      },
      {
        id: "construction",
        numero: 2,
        jours: "Jours 3 à 8",
        titre: "Interventions sur le site de test",
        intro: "Tout se fait dans votre WordPress et votre constructeur de pages, sans changer d'outil.",
        actions: [
          ...when(has("wp-mises-a-jour"), "Mises à jour de WordPress, du thème et des extensions, testées page par page."),
          ...when(has("wp-nettoyage"), "Retrait des extensions inutiles ou en double."),
          ...when(has("wp-arborescence"), "Menu simplifié et pages réorganisées."),
          ...when(has("wp-accueil"), "Page d'accueil refaite dans votre constructeur."),
          ...when(has("wp-page") || has("wp-section"), "Pages et sections demandées créées ou reprises, dans votre charte."),
          ...when(has("wp-correction"), "Chaque correction de votre liste traitée et vérifiée une par une."),
          ...when(has("wp-formulaire"), "Formulaire avec vos champs métier, anti-spam, notification testée jusqu'à votre boîte e-mail."),
          ...when(has("wp-performance"), "Images compressées, cache et chargement allégé."),
          ...when(has("wp-blog"), "Structure du blog : page d'archive et modèle d'article."),
        ],
        vousValidez: null,
        paiements: [],
        process: [
          {
            titre: "Vous voyez le travail avancer",
            texte: "Le site de test est accessible à tout moment : vous vérifiez chaque modification vous-même, sans attendre un compte rendu.",
          },
          {
            titre: "Un seul interlocuteur",
            texte: "La personne qui vous répond est celle qui intervient sur votre site. Rien n'est sous-traité.",
          },
        ],
      },
      {
        id: "lancement",
        numero: 3,
        jours: "Jours 9 et 10",
        titre: "Référencement et mise en production",
        intro: "Les derniers réglages, votre validation, puis le passage sur votre site réel.",
        actions: [
          ...when(has("wp-seo-base"), "Titres, descriptions, structure des titres et textes alternatifs repris page par page."),
          ...when(has("wp-seo-local"), "Fiche Google Business Profile créée ou complétée, balisage local."),
          "Vérifications finales sur mobile, tablette et ordinateur.",
          "Passage des modifications du site de test à votre site réel, puis nouvelle vérification en ligne.",
          "Une heure de prise en main en visio sur vos propres pages, puis un aide-mémoire écrit.",
        ],
        vousValidez: "Le site de test, avant toute mise en production.",
        paiements: [p.fin],
        process: [
          {
            titre: "Rien en production sans votre accord",
            texte: "Vous validez sur le site de test. Tant que vous n'avez pas dit oui, votre site en ligne reste tel qu'il est.",
          },
        ],
      },
    ];
  }

  return [
    {
      id: "cadrage",
      numero: 1,
      jours: "Jours 1 à 3",
      titre: "Cadrage et maquette",
      intro: "On fixe ensemble ce que fait chaque page, puis vous voyez votre site avant qu'il soit construit.",
      actions: [
        "On passe en revue l'arborescence proposée : chaque page, son contenu, et ce qu'elle doit amener le visiteur à faire.",
        "Vous recevez la liste des contenus à me transmettre (textes, photos, logo, accès au nom de domaine), page par page.",
        ...when(has("reprise-contenu"), "J'inventorie les pages et les adresses de votre site actuel, pour que rien ne se perde pendant la refonte."),
        "Je dessine la maquette de votre page d'accueil : mise en page, couleurs, typographie, avec vos contenus réels quand ils existent.",
      ],
      vousValidez: "La maquette, avec deux séries de corrections incluses.",
      paiements: [p.commande, p.milieu],
      process: [
        {
          titre: "La maquette avant le code",
          texte: "Vous validez l'apparence de votre site avant la première ligne de code. Les changements de fond se font à ce moment-là, quand ils ne coûtent rien.",
        },
      ],
    },
    {
      id: "construction",
      numero: 2,
      jours: "Jours 4 à 8",
      titre: "Construction du site",
      intro: "Le site se construit d'après la maquette validée, et vous pouvez l'ouvrir dès le premier jour.",
      actions: [
        "Chaque page construite d'après la maquette, et adaptée au mobile, à la tablette et à l'ordinateur.",
        "Votre espace d'administration : chaque texte, image et page que vous voudrez changer y est modifiable, sans toucher au code.",
        ...when(has("formulaire-metier"), "Formulaire avec vos champs métier : anti-spam, consentement RGPD, notification testée jusqu'à votre boîte e-mail."),
        ...when(has("formulaire-contact"), "Formulaire de contact avec protection anti-spam, messages testés jusqu'à votre boîte e-mail."),
        ...when(has("estimateur"), "Estimateur en ligne, avec un barème que vous réglez vous-même."),
        ...when(has("galerie"), "Galerie de réalisations que vous alimentez vous-même."),
        ...when(has("carte"), "Carte ou annuaire de vos établissements."),
        ...when(has("blog"), "Rubrique actualités ou blog, prête à publier."),
        ...when(has("reprise-contenu"), "Reprise des textes et images de votre site actuel."),
        "Mentions légales, politique de confidentialité et bandeau cookies.",
      ],
      vousValidez: null,
      paiements: [],
      process: [
        {
          titre: "Votre site en direct, pendant qu'il se construit",
          texte: "Il tourne en préproduction à une adresse privée. Vous l'ouvrez sur votre téléphone quand vous voulez, sans attendre un compte rendu ou une démo.",
        },
        {
          titre: "Un seul interlocuteur",
          texte: "La personne qui vous répond est celle qui construit votre site. Rien n'est sous-traité, rien ne se perd entre deux intermédiaires.",
        },
      ],
    },
    {
      id: "lancement",
      numero: 3,
      jours: "Jours 9 et 10",
      titre: "Visibilité, validation et mise en ligne",
      intro: "Votre site est préparé pour être trouvé, vous le validez, puis il part en ligne.",
      actions: [
        ...when(has("seo-base"), "Chaque page reçoit son titre et sa description pour Google, le plan du site est déclaré, la vitesse est vérifiée."),
        ...when(has("seo-local"), "Fiche Google Business Profile créée ou complétée, avec la même adresse partout."),
        ...when(has("visibilite-ia"), "Données structurées et questions-réponses courtes, lisibles par ChatGPT et les réponses IA de Google."),
        ...when(has("redirections"), "Les anciennes adresses de votre site renvoient vers les nouvelles pages : votre référencement actuel n'est pas perdu."),
        "Vérifications finales : affichage sur mobile, tablette et ordinateur, formulaires, liens.",
        "Mise en ligne sur votre nom de domaine, certificat de sécurité et sauvegardes en place.",
        "Une heure de prise en main en visio, puis un aide-mémoire écrit de vos gestes courants.",
      ],
      vousValidez: "Le site complet en préproduction, avant sa mise en ligne.",
      paiements: [p.fin],
      process: [
        {
          titre: "Rien en ligne sans votre accord",
          texte: "Vous relisez le site complet. Tant que vous n'avez pas dit oui, il ne part pas en ligne, et le solde n'est pas dû.",
        },
        {
          titre: "Tout est à votre nom",
          texte: "Nom de domaine, comptes et contenus vous appartiennent dès le premier jour. Si un jour vous voulez changer de prestataire, vous partez avec tout.",
        },
      ],
    },
  ];
}

// Version anglaise du déroulé (pages en anglais, 2026-10-15) : mêmes jalons,
// mêmes conditions d'affichage que la version française ci-dessus.
function jalonsEn(
  kind: VitrineCatalogueKind,
  has: (id: string) => boolean,
  p: { commande: string; milieu: string; fin: string },
  when: (cond: boolean, text: string) => string[]
): Jalon[] {
  if (kind === "wordpress") {
    return [
      {
        id: "cadrage",
        numero: 1,
        jours: "Days 1 and 2",
        titre: "Audit and staging site",
        intro: "Before touching anything, I secure your site and measure where it stands.",
        actions: [
          "Full backup of your site: files, database, settings.",
          "Copy of the site on a protected, non-indexed subdomain: that is where I work, so your live site is never disrupted.",
          "Audit of the theme, plugins and loading speed, with Google's measurements.",
          "A written work plan, item by item, based on your list of requests.",
        ],
        vousValidez: "The work plan.",
        paiements: [p.commande, p.milieu],
        process: [
          {
            titre: "A backup before anything else",
            texte: "Nothing is changed on your site until a full copy exists. If anything goes wrong, we roll back.",
          },
        ],
      },
      {
        id: "construction",
        numero: 2,
        jours: "Days 3 to 8",
        titre: "Work on the staging site",
        intro: "Everything is done in your WordPress and your page builder, without changing tools.",
        actions: [
          ...when(has("wp-mises-a-jour"), "WordPress, theme and plugin updates, tested page by page."),
          ...when(has("wp-nettoyage"), "Unused or duplicate plugins removed."),
          ...when(has("wp-arborescence"), "Simplified menu and reorganised pages."),
          ...when(has("wp-accueil"), "Home page rebuilt in your page builder."),
          ...when(has("wp-page") || has("wp-section"), "Requested pages and sections created or reworked, in your brand style."),
          ...when(has("wp-correction"), "Each fix on your list handled and checked one by one."),
          ...when(has("wp-formulaire"), "Form with your business fields, spam protection, notification tested all the way to your inbox."),
          ...when(has("wp-performance"), "Compressed images, caching and lighter loading."),
          ...when(has("wp-blog"), "Blog structure: archive page and article template."),
        ],
        vousValidez: null,
        paiements: [],
        process: [
          {
            titre: "You see the work progress",
            texte: "The staging site is accessible at any time: you check every change yourself, without waiting for a report.",
          },
          {
            titre: "A single point of contact",
            texte: "The person who answers you is the one working on your site. Nothing is subcontracted.",
          },
        ],
      },
      {
        id: "lancement",
        numero: 3,
        jours: "Days 9 and 10",
        titre: "SEO and go-live",
        intro: "The final adjustments, your approval, then the move to your live site.",
        actions: [
          ...when(has("wp-seo-base"), "Titles, descriptions, heading structure and alt texts reworked page by page."),
          ...when(has("wp-seo-local"), "Google Business Profile listing created or completed, local markup."),
          "Final checks on mobile, tablet and desktop.",
          "Changes moved from the staging site to your live site, then checked again online.",
          "One hour of onboarding by video call on your own pages, then a written quick guide.",
        ],
        vousValidez: "The staging site, before anything goes live.",
        paiements: [p.fin],
        process: [
          {
            titre: "Nothing goes live without your approval",
            texte: "You approve on the staging site. Until you say yes, your live site stays exactly as it is.",
          },
        ],
      },
    ];
  }

  return [
    {
      id: "cadrage",
      numero: 1,
      jours: "Days 1 to 3",
      titre: "Scoping and mockup",
      intro: "Together we set what each page does, then you see your site before it is built.",
      actions: [
        "We go through the proposed site structure: each page, its content, and what it should lead the visitor to do.",
        "You receive the list of content to send me (texts, photos, logo, access to the domain name), page by page.",
        ...when(has("reprise-contenu"), "I list the pages and addresses of your current site, so that nothing is lost during the redesign."),
        "I design the mockup of your home page: layout, colours, typography, with your real content where it exists.",
      ],
      vousValidez: "The mockup, with two rounds of changes included.",
      paiements: [p.commande, p.milieu],
      process: [
        {
          titre: "The mockup before the code",
          texte: "You approve the look of your site before the first line of code. Major changes happen at this stage, when they cost nothing.",
        },
      ],
    },
    {
      id: "construction",
      numero: 2,
      jours: "Days 4 to 8",
      titre: "Building the site",
      intro: "The site is built from the approved mockup, and you can open it from day one.",
      actions: [
        "Each page built from the mockup, and adapted to mobile, tablet and desktop.",
        "Your admin area: every text, image and page you will want to change can be edited there, without touching the code.",
        ...when(has("formulaire-metier"), "Form with your business fields: spam protection, GDPR consent, notification tested all the way to your inbox."),
        ...when(has("formulaire-contact"), "Contact form with spam protection, messages tested all the way to your inbox."),
        ...when(has("estimateur"), "Online estimator, with a rate card you set yourself."),
        ...when(has("galerie"), "Project gallery that you add to yourself."),
        ...when(has("carte"), "Map or directory of your locations."),
        ...when(has("blog"), "News or blog section, ready to publish."),
        ...when(has("reprise-contenu"), "Texts and images taken over from your current site."),
        "Legal notice, privacy policy and cookie banner.",
      ],
      vousValidez: null,
      paiements: [],
      process: [
        {
          titre: "Your site live, while it is being built",
          texte: "It runs on a private staging address. You open it on your phone whenever you like, without waiting for a report or a demo.",
        },
        {
          titre: "A single point of contact",
          texte: "The person who answers you is the one building your site. Nothing is subcontracted, nothing gets lost between intermediaries.",
        },
      ],
    },
    {
      id: "lancement",
      numero: 3,
      jours: "Days 9 and 10",
      titre: "Visibility, approval and go-live",
      intro: "Your site is prepared to be found, you approve it, then it goes live.",
      actions: [
        ...when(has("seo-base"), "Each page gets its title and description for Google, the sitemap is submitted, speed is checked."),
        ...when(has("seo-local"), "Google Business Profile listing created or completed, with the same address everywhere."),
        ...when(has("visibilite-ia"), "Structured data and short questions and answers, readable by ChatGPT and Google's AI answers."),
        ...when(has("redirections"), "The old addresses of your site point to the new pages: your current search ranking is not lost."),
        "Final checks: display on mobile, tablet and desktop, forms, links.",
        "Go-live on your domain name, security certificate and backups in place.",
        "One hour of onboarding by video call, then a written quick guide to your everyday tasks.",
      ],
      vousValidez: "The complete site on staging, before it goes live.",
      paiements: [p.fin],
      process: [
        {
          titre: "Nothing goes live without your approval",
          texte: "You review the complete site. Until you say yes, it doesn't go live, and the balance isn't due.",
        },
        {
          titre: "Everything is in your name",
          texte: "Domain name, accounts and content belong to you from day one. If you ever want to change provider, you leave with everything.",
        },
      ],
    },
  ];
}

/** Postes de base regroupés par jalon (ordre du catalogue conservé), options à part. */
export function groupPricingByJalon(pricing: Pick<VitrinePricing, "lines">): {
  groupes: { id: JalonId; lines: VitrinePricingLine[] }[];
  options: VitrinePricingLine[];
} {
  const ordre: JalonId[] = ["cadrage", "construction", "lancement"];
  const base = pricing.lines.filter((l) => l.kind === "base");
  return {
    groupes: ordre
      .map((id) => ({ id, lines: base.filter((l) => jalonDuLot(l.lotId) === id) }))
      .filter((g) => g.lines.length > 0),
    options: pricing.lines.filter((l) => l.kind === "option"),
  };
}
